import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { enumLabel, enumOptions } from '../../lib/enums';
import { formatMoney, type NumberInputValue, typedNumber } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { AREAS_PATH, type StorageAreaRow, useStockItems, useStockOnHand, useStorageAreas } from './stock';

const ROOMS_PATH = '/admin/v1/inventory/storage-rooms';

interface Room {
  id: string;
  name: string;
  displayOrder: number;
  active: boolean;
}

interface RoomDraft {
  id?: string;
  name: string;
  displayOrder: NumberInputValue;
  active: boolean;
}

interface AreaDraft {
  id?: string;
  storageRoomId: string;
  name: string;
  kind: string;
  active: boolean;
}

const BLANK_AREA = (storageRoomId: string): AreaDraft => ({
  storageRoomId,
  name: '',
  kind: 'shelf',
  active: true,
});

/**
 * Where stock is kept, laid out the way the building is.
 *
 * This replaces two tables that were two database tables: one listing rooms, one listing areas,
 * with a "which room?" dropdown on the second and a "what is in it?" column on the first, both of
 * them there only to stitch back together what the split had taken apart. Nobody has ever needed a
 * list of every shelf in the business sorted by name — the question is always "what is in the
 * bodega", and the answer is the room with its shelves under it.
 *
 * A room and its areas are also not really two decisions. Adding a fridge starts by pressing "Add
 * a fridge or shelf" inside the room it is standing in, so the room is answered by where you
 * pressed rather than asked for again in a dropdown of every room in the building.
 */
export function StorageLayout({ locationId, currency }: { locationId?: string; currency: string }) {
  const queryClient = useQueryClient();
  const [roomDraft, setRoomDraft] = useState<RoomDraft | null>(null);
  const [areaDraft, setAreaDraft] = useState<AreaDraft | null>(null);

  const roomsQuery = useQuery({
    queryKey: listKey(ROOMS_PATH),
    queryFn: () => rawApi<Room[]>('GET', ROOMS_PATH),
    staleTime: 60_000,
  });
  const areas = useStorageAreas();
  const onHand = useStockOnHand();
  const stock = useStockItems();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(ROOMS_PATH) });
    void queryClient.invalidateQueries({ queryKey: listKey(AREAS_PATH) });
  };

  const saveRoom = useMutation({
    mutationFn: (draft: RoomDraft) => {
      const body = {
        name: draft.name.trim(),
        displayOrder: typedNumber(draft.displayOrder) ?? 0,
        active: draft.active,
      };
      return draft.id
        ? rawApi('PATCH', `${ROOMS_PATH}/${draft.id}`, body)
        : rawApi('POST', ROOMS_PATH, { ...body, locationId });
    },
    onSuccess: (_data, draft) => {
      refresh();
      setRoomDraft(null);
      notifyDone(draft.id ? 'Saved' : 'Added', `${draft.name.trim()} is up to date.`);
    },
    onError: (err: Error) => notifyError(err, 'save this room'),
  });

  const saveArea = useMutation({
    mutationFn: (draft: AreaDraft) => {
      const body = {
        storageRoomId: draft.storageRoomId,
        name: draft.name.trim(),
        kind: draft.kind,
        active: draft.active,
      };
      return draft.id
        ? rawApi('PATCH', `${AREAS_PATH}/${draft.id}`, body)
        : rawApi('POST', AREAS_PATH, { ...body, locationId });
    },
    onSuccess: (_data, draft) => {
      refresh();
      setAreaDraft(null);
      notifyDone(
        draft.id ? 'Saved' : 'Added',
        draft.id
          ? `${draft.name.trim()} is up to date.`
          : `${draft.name.trim()} can now be counted, and stock can be put away in it.`,
      );
    },
    onError: (err: Error) => notifyError(err, 'save this area'),
  });

  const rooms = [...(roomsQuery.data ?? [])].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
  );
  // Areas whose room has gone, or that were written straight through the API without one. They
  // still hold stock and still have to be reachable, so they get a section of their own rather
  // than dropping off a screen that claims to show everywhere stock is kept.
  const homeless = areas.rows.filter((a) => !a.storageRoomId || !rooms.some((r) => r.id === a.storageRoomId));

  const holdingIn = (areaId: string) => {
    const held = onHand.rows.filter((s) => s.storageLocationId === areaId && s.qtyOnHand !== 0);
    return {
      count: held.length,
      worthMinor: held.reduce((sum, s) => sum + Math.round(s.qtyOnHand * stock.costOf(s.inventoryItemId)), 0),
    };
  };

  const loading = roomsQuery.isLoading || areas.isLoading;

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Group justify="space-between" mb="sm" wrap="nowrap" align="flex-start">
        <div>
          <Title order={5}>Where stock is kept</Title>
          <Text size="sm" c="dimmed" mt={2}>
            Each room and the places inside it. Stock is counted one area at a time, so the kitchen and the
            bar can be done on different days — and a figure against "the shelf" when there is one in all
            three rooms is a figure nobody can chase.
          </Text>
        </div>
        <Button
          size="sm"
          onClick={() => setRoomDraft({ name: '', displayOrder: rooms.length, active: true })}
        >
          Add a room
        </Button>
      </Group>

      {roomsQuery.isError || areas.isError ? (
        <Alert color="red" title="Could not load this">
          <Text size="sm" mb="sm">
            The rooms and areas could not be reached. Nothing has been lost — this is only a display problem.
          </Text>
          <Button size="xs" variant="light" onClick={refresh}>
            Try again
          </Button>
        </Alert>
      ) : loading ? (
        <Group gap="xs" py="xl" justify="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading…
          </Text>
        </Group>
      ) : rooms.length === 0 && homeless.length === 0 ? (
        <Text size="sm" c="dimmed" py="lg">
          Nowhere is set up yet. Start with the rooms you keep stock in — the bar, the kitchen, the bodega —
          then add the shelves and fridges inside each one.
        </Text>
      ) : (
        <Stack gap="md">
          {rooms.map((room) => (
            <RoomCard
              key={room.id}
              title={room.name}
              closed={!room.active}
              areas={areas.rows.filter((a) => a.storageRoomId === room.id)}
              holdingIn={holdingIn}
              currency={currency}
              onEditRoom={() =>
                setRoomDraft({
                  id: room.id,
                  name: room.name,
                  displayOrder: room.displayOrder,
                  active: room.active,
                })
              }
              onAddArea={() => setAreaDraft(BLANK_AREA(room.id))}
              onEditArea={(area) =>
                setAreaDraft({
                  id: area.id,
                  storageRoomId: area.storageRoomId ?? room.id,
                  name: area.name,
                  kind: area.kind || 'shelf',
                  active: area.active,
                })
              }
            />
          ))}

          {homeless.length > 0 && (
            <RoomCard
              title="Not in any room"
              note="Put each of these in a room so it appears on that room's count."
              areas={homeless}
              holdingIn={holdingIn}
              currency={currency}
              onEditArea={(area) =>
                setAreaDraft({
                  id: area.id,
                  storageRoomId: area.storageRoomId ?? '',
                  name: area.name,
                  kind: area.kind || 'shelf',
                  active: area.active,
                })
              }
            />
          )}
        </Stack>
      )}

      <Modal
        opened={roomDraft !== null}
        onClose={() => setRoomDraft(null)}
        title={roomDraft?.id ? `Edit ${roomDraft.name || 'this room'}` : 'Add a room'}
        size="md"
        closeOnClickOutside={false}
      >
        {roomDraft && (
          <Stack gap="md">
            <TextInput
              label="Name"
              required
              placeholder="The bodega"
              value={roomDraft.name}
              onChange={(e) => setRoomDraft({ ...roomDraft, name: e.currentTarget.value })}
            />
            <NumberInput
              label="Where it comes in the list"
              description="Lowest first — put the rooms in the order you walk them when you count"
              min={0}
              value={roomDraft.displayOrder}
              onChange={(v) => setRoomDraft({ ...roomDraft, displayOrder: v })}
            />
            {roomDraft.id && (
              <Checkbox
                label="Still in use"
                description="Untick a room you no longer keep stock in — nothing counted in it is lost"
                checked={roomDraft.active}
                onChange={(e) => setRoomDraft({ ...roomDraft, active: e.currentTarget.checked })}
              />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setRoomDraft(null)} disabled={saveRoom.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => saveRoom.mutate(roomDraft)}
                loading={saveRoom.isPending}
                disabled={!roomDraft.name.trim()}
              >
                {roomDraft.id ? 'Save changes' : 'Add the room'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={areaDraft !== null}
        onClose={() => setAreaDraft(null)}
        title={areaDraft?.id ? `Edit ${areaDraft.name || 'this area'}` : 'Add somewhere stock is kept'}
        size="md"
        closeOnClickOutside={false}
      >
        {areaDraft && (
          <Stack gap="md">
            <TextInput
              label="Name"
              required
              // No room in the example: the room is the card this was opened from, and a name like
              // "Bar back cooler" would put the room in twice on every screen that shows both.
              placeholder="Back cooler"
              value={areaDraft.name}
              onChange={(e) => setAreaDraft({ ...areaDraft, name: e.currentTarget.value })}
            />
            <Select
              label="What it is"
              // "Bar" is not offered: it says which room, and the room is answered by where this
              // was opened. Anything already recorded under it still reads back as "Bar".
              data={enumOptions('storageKind').filter((o) => o.value !== 'bar')}
              value={areaDraft.kind}
              onChange={(v) => setAreaDraft({ ...areaDraft, kind: v ?? 'shelf' })}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              description="Change this later if the place itself changes — a back room that becomes a walk-in keeps everything ever counted in it"
            />
            {/* Only when there is a reason to ask: moving a shelf to another room, or giving a room
                to one that has none. Adding inside a room already answered this. */}
            {(areaDraft.id || !areaDraft.storageRoomId) && (
              <Select
                label="Which room it is in"
                required
                data={rooms.map((r) => ({ value: r.id, label: r.name }))}
                value={areaDraft.storageRoomId || null}
                onChange={(v) => setAreaDraft({ ...areaDraft, storageRoomId: v ?? '' })}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
              />
            )}
            {areaDraft.id && (
              <Checkbox
                label="Still in use"
                description="Untick somewhere you no longer keep stock — what was counted there is kept"
                checked={areaDraft.active}
                onChange={(e) => setAreaDraft({ ...areaDraft, active: e.currentTarget.checked })}
              />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setAreaDraft(null)} disabled={saveArea.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => saveArea.mutate(areaDraft)}
                loading={saveArea.isPending}
                disabled={!areaDraft.name.trim() || !areaDraft.storageRoomId}
              >
                {areaDraft.id ? 'Save changes' : 'Add it'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Paper>
  );
}

/** One room and everywhere inside it. */
function RoomCard({
  title,
  note,
  closed,
  areas,
  holdingIn,
  currency,
  onEditRoom,
  onAddArea,
  onEditArea,
}: {
  title: string;
  note?: string;
  closed?: boolean;
  areas: StorageAreaRow[];
  holdingIn: (areaId: string) => { count: number; worthMinor: number };
  currency: string;
  onEditRoom?: () => void;
  onAddArea?: () => void;
  onEditArea: (area: StorageAreaRow) => void;
}) {
  const inside = [...areas].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start" mb={inside.length ? 'xs' : 0}>
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <Text fw={600}>{title}</Text>
          {closed && (
            <Badge size="xs" variant="light" color="gray">
              No longer in use
            </Badge>
          )}
          {note && (
            <Text size="xs" c="dimmed">
              {note}
            </Text>
          )}
        </Group>
        <Group gap="xs" wrap="nowrap">
          {onAddArea && (
            <Button size="compact-xs" variant="light" onClick={onAddArea}>
              Add a shelf or fridge
            </Button>
          )}
          {onEditRoom && (
            <Button size="compact-xs" variant="subtle" onClick={onEditRoom}>
              Edit room
            </Button>
          )}
        </Group>
      </Group>

      {inside.length === 0 ? (
        <Text size="sm" c="dimmed">
          Nothing in here yet. Add the shelves, fridges and freezers that stock actually sits on — each one is
          counted separately.
        </Text>
      ) : (
        <Stack gap={2}>
          {inside.map((area) => {
            const held = holdingIn(area.id);
            return (
              <Group key={area.id} justify="space-between" wrap="nowrap" gap="sm" py={4}>
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500}>
                    {area.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {enumLabel('storageKind', area.kind)}
                  </Text>
                  {!area.active && (
                    <Badge size="xs" variant="light" color="gray">
                      No longer in use
                    </Badge>
                  )}
                </Group>
                <Group gap="md" wrap="nowrap">
                  {/* What is actually in there, so somewhere nobody has ever counted is obvious
                      from here rather than from opening a count and finding an empty sheet. */}
                  <Text size="xs" c="dimmed" ta="right">
                    {held.count === 0
                      ? 'Nothing in it'
                      : `${held.count} ${held.count === 1 ? 'item' : 'items'} · ${formatMoney(held.worthMinor, currency)}`}
                  </Text>
                  <Button size="compact-xs" variant="subtle" onClick={() => onEditArea(area)}>
                    Edit
                  </Button>
                </Group>
              </Group>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
